import React, { useCallback, useEffect, useMemo, useState } from "react";

import { mdiPlaylistPlus, mdiTrashCanOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { useQuery } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import FASTPlaylistDropZone from "./FASTPlaylistDropZone";
import FASTPlaylistFormHeader from "./FASTPlaylistFormHeader";
import FastAPI from "../../../api/FastAPI";
import useFASTPlaylistMutations from "../../../hooks/mutations/useFASTPlaylistMutations";
import useFASTLayout from "../../../hooks/useFASTLayout";
import Block from "../../block/Block";
import Button from "../../button/Button";
import { CONTENT_TYPE } from "../../content/Type";
import NameHeaderInput from "../../form/NameHeaderInput";
import SaveButton from "../../form/SaveButton";

import type { JSX } from "react";

import type {
  GetFastIndexMediaListResponse,
  IndexFastMediaItem,
} from "../../../api/MediaAPI.types";
import type { PlaylistDropzoneMedia } from "../../../types/fast";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";

interface FASTPlaylistFormProps {
  onAddAllFilteredMedia: () => Promise<GetFastIndexMediaListResponse>;
  isAddFilteredMediaDisabled: boolean;
}

export default function FASTPlaylistForm({
  onAddAllFilteredMedia,
  isAddFilteredMediaDisabled,
}: FASTPlaylistFormProps): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");

  const [mediaList, setMediaList] = useState<PlaylistDropzoneMedia[]>();
  const { channelId, playlistId } = useParams<
    FASTChannelRoutesParams & {
      playlistId: string;
    }
  >();

  const isNewPlaylist = playlistId === "new-playlist";

  const getPlaylistQuery = FastAPI.generateQuery({
    fn: FastAPI.getPlaylist,
    params: [
      {
        channelId,
        playlistId,
      },
    ],
  });

  const { data: getPlaylistData, isFetching } = useQuery({
    ...getPlaylistQuery,
    enabled: !isNewPlaylist,
  });

  const { updateFASTPlaylist } = useFASTPlaylistMutations();

  const initialMediaList = useMemo(
    () =>
      getPlaylistData?.items
        .map((playlistItems) => ({
          uuid: uuidv4(),
          formatKey: playlistItems.format,
          mediaId: playlistItems.media_uuid,
          duration: playlistItems.duration,
          label: playlistItems.label,
          rank: playlistItems.rank,
          type: CONTENT_TYPE.HOSTED,
        }))
        .sort((a, b) => a.rank - b.rank),
    [getPlaylistData?.items],
  );

  useEffect(() => {
    if (mediaList) return;

    setMediaList(initialMediaList);
  }, [mediaList, initialMediaList]);

  const methods = useForm({
    values: {
      name: isNewPlaylist ? t("newPlaylist") : (getPlaylistData?.name ?? ""),
    },
  });

  const handlePlaylistChange = (
    newMediaList: PlaylistDropzoneMedia[],
  ): void => {
    setMediaList(newMediaList);
  };

  const handleFormSubmit = (name: string): void => {
    updateFASTPlaylist.mutate({
      updatedPlaylist: {
        name,
        items:
          mediaList?.map((media) => ({
            media_uuid: media.mediaId,
            rank: media.rank,
          })) ?? [],
      },
      isNewPlaylist,
      mediaList,
    });
  };

  const handleRemoveAll = (): void => {
    setMediaList([]);
  };

  const mediaToDropzone = (
    media: IndexFastMediaItem,
    rank: number,
  ): PlaylistDropzoneMedia => ({
    uuid: uuidv4(),
    label: media.label,
    mediaId: media.uuid,
    duration: media.source_duration,
    formatKey:
      media.format && typeof media.format !== "string"
        ? media.format.key
        : undefined,
    type: media.type,
    rank,
  });

  const formId = "playlist-edit-form";

  useFASTLayout({
    title: <NameHeaderInput control={methods.control} />,
    button: <SaveButton formId={formId} />,
    breadcrumbs: [
      {
        name: t("fast-playlists"),
        url: `/fast-channels/${channelId}/fast-playlists`,
      },
      {
        name: isNewPlaylist ? t("newPlaylist") : (getPlaylistData?.name ?? ""),
      },
    ],
  });

  const isEmptyPlaylist =
    mediaList?.length === 0 && initialMediaList?.length === 0;

  const handleClick = useCallback(async () => {
    try {
      const response = await onAddAllFilteredMedia();
      const startRank = mediaList?.length ?? 0;
      const newItemList = response.data
        .filter((media) => !mediaList?.some((m) => m.mediaId === media.uuid))
        .map((media, index) => mediaToDropzone(media, startRank + index));
      setMediaList((prev) => [...(prev ?? []), ...newItemList]);
    } catch {
      console.error("error adding media list");
    }
  }, [onAddAllFilteredMedia, mediaList]);

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...methods}>
      <form
        className="flex w-full flex-col"
        id={formId}
        onSubmit={methods.handleSubmit((data) => handleFormSubmit(data.name))}
      >
        <Block
          isFullSize
          headerComponent={<FASTPlaylistFormHeader mediaList={mediaList} />}
          title={t("fastPlaylistEditTitle")}
        >
          <div className="flex h-full flex-col gap-[0.78125rem]">
            <div className="flex grow-0 justify-between">
              <Button
                data-tooltip-hidden={!isAddFilteredMediaDisabled}
                data-tooltip-id="add-active-selection-tooltip"
                isDisabled={isAddFilteredMediaDisabled}
                onClick={handleClick}
                size="md"
                type="button"
                variant="secondary"
                data-tooltip-content={
                  isAddFilteredMediaDisabled
                    ? t("addFilteredMediaDisabledTooltip")
                    : undefined
                }
              >
                <Icon
                  className="text-primary"
                  path={mdiPlaylistPlus}
                  size={1}
                />
                {t("addActiveSelection")}
              </Button>
              <Button
                isDisabled={isEmptyPlaylist}
                onClick={handleRemoveAll}
                size="md"
                type="button"
                variant="secondary"
              >
                <Icon color="#D92D20" path={mdiTrashCanOutline} size={1} />
                {t("removeAll", { ns: "common" })}
              </Button>
            </div>
            <FASTPlaylistDropZone
              initialMediaList={mediaList ?? initialMediaList}
              isLoading={isFetching}
              onPlaylistChange={handlePlaylistChange}
            />
          </div>
        </Block>
      </form>
    </FormProvider>
  );
}
